import React, { useState, useEffect, useRef } from 'react';
import { useUrlState } from "@/lib/useUrlState";
import { Upload, MessageSquare, FileText, Menu, X, Play, CheckCircle, Download, File, Calendar, AlertTriangle, Clock, Archive, Trash2, RefreshCw, Save, Copy, Mail, LogOut, User, FileSearch } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { JsonView, darkStyles, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import { useAuth } from "@/lib/AuthContext";
import SubTabs from "@/components/SubTabs";
import { DocButton, DocViewer, currentOf, authed } from "./DocControls";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";

// Every fetch below is the original Contract Admin code. This shim adds the host
// app's JumpCloud bearer token so its endpoints stay behind the same RBAC.
const fetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  if (!token) return window.fetch(url, options);
  return window.fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
  });
};

// One in-flight/most-recent session request shared across mounts of this tab.
let openSessionRequest = null;

// --- Components ---

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-sm border border-slate-200  ${className}`}>
    {children}
  </div>
);

const CA_TABS = [
  { id: 'chat', label: 'Chat' }, { id: 'summary', label: 'Summary' }, { id: 'terms', label: 'Terms' },
  { id: 'scope', label: 'Scope' }, { id: 'exclusions', label: 'Exclusions' }, { id: 'email', label: 'Email' },
  { id: 'history', label: 'History' },
];

// Current-review search: count + paint matches with the CSS Custom Highlight API (no DOM mutation).
const paintMatches = (root, q) => {
  const counts = {};
  const ranges = [];
  if (!root || !q) { if (window.CSS?.highlights) CSS.highlights.delete('ca-search'); return counts; }
  const needle = q.toLowerCase();
  root.querySelectorAll('[data-ca-pane]').forEach((pane) => {
    const walker = document.createTreeWalker(pane, NodeFilter.SHOW_TEXT);
    let n = 0;
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const text = node.nodeValue.toLowerCase();
      for (let i = text.indexOf(needle); i !== -1; i = text.indexOf(needle, i + needle.length)) {
        n += 1;
        const r = new Range(); r.setStart(node, i); r.setEnd(node, i + needle.length); ranges.push(r);
      }
    }
    counts[pane.dataset.caPane] = n;
  });
  if (window.CSS?.highlights && window.Highlight) CSS.highlights.set('ca-search', new Highlight(...ranges));
  return counts;
};

const SummaryView = ({ data }) => {
    if (!data) return null;

    const fields = [
        { key: "project_name", label: "Project Name" },
        { key: "general_contractor", label: "General Contractor" },
        { key: "owner", label: "Owner" },
        { key: "architect", label: "Architect" },
        { key: "project_address", label: "Project Address" },
        { key: "total_contract_value", label: "Total Contract Value" },
        { key: "project_start_date", label: "Start Date" },
        { key: "project_substantial_completion", label: "Substantial Completion" },
        { key: "pay_app_due_date", label: "Pay App Due Date" },
        { key: "retention_percent", label: "Retention %" },
        { key: "prevailing_wage", label: "Prevailing Wage" },
        { key: "tax_status", label: "Tax Rate / Exempt" },
        { key: "parking", label: "Parking" },
        { key: "ocip_ccip_status", label: "OCIP / CCIP" },
        { key: "insurance_compliance", label: "Insurance Compliance" }
    ];

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Project Summary</h2>
            <div className="grid grid-cols-1 gap-4">
                {fields.map((field) => {
                    const value = data[field.key];
                    let valueClass = "text-slate-900";
                    let containerClass = "bg-white p-4 rounded border border-slate-200";

                    // Special styling for Insurance Compliance
                    if (field.key === "insurance_compliance") {
                        if (value?.includes("Not Compliant")) {
                            valueClass = "text-red-700 font-bold";
                            containerClass = "bg-red-50 p-4 rounded border border-red-200";
                        } else if (value?.includes("Compliant")) {
                            valueClass = "text-green-700 font-bold";
                            containerClass = "bg-green-50 p-4 rounded border border-green-200";
                        } else if (value?.includes("Cannot Be Confirmed")) {
                            valueClass = "text-yellow-700 font-bold";
                            containerClass = "bg-yellow-50 p-4 rounded border border-yellow-200";
                        }
                    }

                    // Special styling for Parking
                    if (field.key === "parking") {
                        if (value?.toLowerCase().includes("fee-based") || value?.toLowerCase().includes("subcontractor responsible")) {
                            valueClass = "text-yellow-700 font-semibold";
                            containerClass = "bg-yellow-50 p-4 rounded border border-yellow-200";
                        } else if (value?.toLowerCase().includes("included") || value?.toLowerCase().includes("provided") || value?.toLowerCase().includes("no cost")) {
                            valueClass = "text-green-700 font-semibold";
                            containerClass = "bg-green-50 p-4 rounded border border-green-200";
                        } else if (value?.toLowerCase().includes("not specified")) {
                            valueClass = "text-slate-500 italic";
                        }
                    }

                    // Special styling for Prevailing Wage
                    if (field.key === "prevailing_wage") {
                        if (value?.toLowerCase() === "yes") {
                            valueClass = "text-blue-700 font-semibold";
                            containerClass = "bg-blue-50 p-4 rounded border border-blue-200";
                        }
                    }

                    // Special styling for OCIP/CCIP
                    if (field.key === "ocip_ccip_status") {
                        if (value?.toLowerCase().includes("specified") || value?.toLowerCase().startsWith("yes")) {
                            valueClass = "text-blue-700 font-semibold";
                            containerClass = "bg-blue-50 p-4 rounded border border-blue-200";
                        }
                    }

                    return (
                        <div key={field.key} className={containerClass}>
                            <div className="text-xs text-slate-500 uppercase tracking-wide mb-1 font-semibold">
                                {field.label}
                            </div>
                            <div className={`text-sm ${valueClass}`}>
                                {value || "Not identified"}
                            </div>
                            {/* Show Insurance details if not compliant */}
                            {field.key === "insurance_compliance" && data.insurance_notes && (
                                <div className="mt-2 text-xs text-slate-600 border-t border-slate-200 pt-2">
                                    {data.insurance_notes}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const NegotiationView = ({ data, isNewClient, onNewClientChange, onJump }) => {
    if (!data) return null;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-slate-900">Negotiation Summary</h2>
                <label className="flex items-center gap-2 cursor-pointer bg-slate-50 px-4 py-2 rounded-sm border border-slate-200 hover:bg-slate-100 transition-colors">
                    <input
                        type="checkbox"
                        checked={isNewClient || false}
                        onChange={(e) => onNewClientChange(e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                        data-testid="new-client-checkbox"
                    />
                    <span className="text-sm font-medium text-slate-700">New Client</span>
                </label>
            </div>
            <div className="space-y-4">
                {data.map((item, idx) => (
                    <Card key={idx} className="overflow-hidden border border-slate-200">
                        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex justify-between items-center">
                            <div className="flex flex-col">
                                <button type="button" onClick={() => onJump?.(item)} title="Show this clause in the contract" data-testid={`ca-term-title-${idx}`}
                                        className="text-left font-bold text-slate-900 hover:text-[#007BFF]">{idx + 1}. {item.title}</button>
                                {item.contract_section && (
                                    <span className="text-sm text-blue-600 font-medium">Contract Section: {item.contract_section}</span>
                                )}
                                {!item.contract_section && item.clause_reference && (
                                    <span className="text-xs text-slate-500 font-mono">{item.clause_reference}</span>
                                )}
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                                item.action?.toLowerCase() === 'strike' ? 'bg-red-100 text-red-700' :
                                item.action?.toLowerCase() === 'modify' ? 'bg-blue-100 text-blue-700' :
                                item.action?.toLowerCase() === 'request for documentation' ? 'bg-purple-100 text-purple-700' :
                                item.action?.toLowerCase() === 'request to incorporate' ? 'bg-green-100 text-green-700' :
                                item.action?.toLowerCase() === 'request acknowledgment' ? 'bg-yellow-100 text-yellow-700' :
                                item.action?.toLowerCase() === 'clarification requested' ? 'bg-orange-100 text-orange-700' :
                                'bg-slate-100 text-slate-700'
                            }`}>
                                {item.action}
                            </span>
                        </div>
                        {onJump && (
                            <button type="button" onClick={() => onJump(item)} data-testid={`ca-term-jump-${idx}`}
                                    className="mx-4 mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#007BFF] hover:underline">
                                <FileSearch className="h-3 w-3" /> Show in contract
                            </button>)}
                        <div className="p-4 space-y-4">
                            <div>
                                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Current Language</div>
                                <div className="bg-white p-3 rounded border border-slate-200 text-sm text-slate-600 italic font-mono">
                                    {item.verbatim_text?.toLowerCase().includes("not addressed") 
                                        ? item.verbatim_text 
                                        : `"${item.verbatim_text}"`}
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <div className="text-xs text-blue-600 uppercase tracking-wide mb-1 font-semibold">Response</div>
                                    <div className="bg-blue-50 p-3 rounded text-sm text-blue-900 font-medium">
                                        {item.clarification || item.proposal_text}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Comment</div>
                                    <div className="text-sm text-slate-700">
                                        {item.comment || item.reason}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
};

// Scope View Component - ALL SCOPES with Section-Level Compliance
const ScopeView = ({ data }) => {
    if (!data) return null;

    const getStatusColor = (status) => {
        if (!status) return "bg-slate-100 text-slate-600";
        if (status === "Compliant" || (status.includes("Aligned") && !status.includes("Not Aligned"))) {
            return "bg-green-100 text-green-700";
        }
        if (status === "Not Compliant" || status.includes("Not Aligned") || status.includes("Corrections Required")) {
            return "bg-red-100 text-red-700";
        }
        if (status.includes("Pending")) {
            return "bg-yellow-100 text-yellow-700";
        }
        return "bg-slate-100 text-slate-600";
    };

    const getSectionStatusBg = (status) => {
        if (status === "Compliant") return "bg-green-500";
        if (status === "Not Compliant") return "bg-red-500";
        return "bg-slate-400";
    };

    const hasIssues = (scope) => {
        if (scope.overall_status === "Not Compliant") return true;
        if (scope.discrepancies && scope.discrepancies.length > 0) return true;
        if (scope.sections) {
            return Object.values(scope.sections).some(s => s.status === "Not Compliant");
        }
        return false;
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const sectionLabels = {
        scope: "Scope",
        price: "Price",
        inclusions: "Inclusions",
        exclusions: "Exclusions",
        material: "Material"
    };

    const getRiskBadgeColor = (risk) => {
        if (risk?.toLowerCase().includes('high')) return 'bg-red-100 text-red-800 border-red-300';
        if (risk?.toLowerCase().includes('med')) return 'bg-orange-100 text-orange-800 border-orange-300';
        return 'bg-green-100 text-green-800 border-green-300';
    };

    const getAlignmentColor = (alignment) => {
        if (alignment === 'ALIGNED') return 'bg-green-100 text-green-800';
        if (alignment === 'PARTIALLY ALIGNED') return 'bg-yellow-100 text-yellow-800';
        return 'bg-red-100 text-red-800';
    };

    // Count stats
    const totalScopes = data.scopes_identified?.length || 0;
    const compliantScopes = data.scopes_identified?.filter(s => s.overall_status === "Compliant").length || 0;
    const outOfScopeCount = data.scopes_identified?.filter(s => s.overall_status === "Out of Scope").length || 0;
    const nonCompliantScopes = totalScopes - compliantScopes - outOfScopeCount;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-slate-900">Scope Review – Contract Admin</h2>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(data.scope_review_status)}`}>
                    {data.scope_review_status || "Pending"}
                </span>
            </div>

            {/* Required Revisions */}
            {data.required_revisions?.length > 0 && (
                <div className="bg-white border-2 border-blue-200 rounded-sm overflow-hidden">
                    <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
                        <span className="font-bold text-blue-900">📝 Required Contract Revisions</span>
                    </div>
                    <div className="divide-y divide-slate-200">
                        {data.required_revisions.map((rev, i) => (
                            <div key={i} className="p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="font-bold text-slate-900">{i + 1}. {rev.title}</span>
                                    <span className={`px-2 py-1 text-xs font-bold rounded border ${getRiskBadgeColor(rev.risk_level)}`}>
                                        {rev.risk_level} Risk
                                    </span>
                                </div>
                                <div className="text-sm text-slate-700">{rev.issue}</div>
                                {rev.gc_ready_language && (
                                    <div className="bg-green-50 p-3 rounded border border-green-200">
                                        <div className="text-xs text-green-700 font-bold mb-1">Please Acknowledge:</div>
                                        <div className="text-sm text-green-900 italic">"{rev.gc_ready_language}"</div>
                                        <button 
                                            onClick={() => copyToClipboard(rev.gc_ready_language)}
                                            className="mt-2 text-xs text-green-600 hover:text-green-800 underline"
                                        >
                                            Copy to clipboard
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Review Mode Info */}
            <div className="bg-slate-50 p-4 rounded-sm border border-slate-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <span className="text-slate-500">Review Mode:</span>
                        <span className="ml-2 font-medium text-slate-900">
                            {data.scope_review_mode === "proposal_only" ? "Proposal Only" : 
                             data.scope_review_mode === "proposal_and_contract" ? "Proposal + Contract Comparison" : 
                             "Awaiting Documents"}
                        </span>
                    </div>
                    <div>
                        <span className="text-slate-500">Total Scopes:</span>
                        <span className="ml-2 font-bold text-slate-900">{totalScopes}</span>
                    </div>
                    <div>
                        <span className="text-slate-500">Proposal:</span>
                        <span className="ml-2 font-medium text-green-600">{data.proposal_filename || "Uploaded"}</span>
                    </div>
                    <div className="flex gap-4">
                        <span className="text-green-600 font-medium">✓ {compliantScopes} Compliant</span>
                        {nonCompliantScopes > 0 && (
                            <span className="text-red-600 font-medium">✗ {nonCompliantScopes} Issues</span>
                        )}
                        {outOfScopeCount > 0 && (
                            <span className="text-slate-500 font-medium">○ {outOfScopeCount} Out of Scope</span>
                        )}
                    </div>
                    {data.contract_filename && (
                        <div className="col-span-2">
                            <span className="text-slate-500">Contract:</span>
                            <span className="ml-2 font-medium text-blue-600">{data.contract_filename}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Legend */}
            <div className="flex gap-4 text-xs">
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-green-500"></div>
                    <span className="text-slate-600">Compliant</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-red-500"></div>
                    <span className="text-slate-600">Not Compliant (Action Required)</span>
                </div>
            </div>

            {/* Scopes List */}
            <div className="space-y-6">
                {data.scopes_identified && data.scopes_identified.map((scope, idx) => (
                    <Card key={idx} className={`overflow-hidden border-2 ${hasIssues(scope) ? 'border-red-300' : 'border-green-300'}`}>
                        {/* Scope Header */}
                        <div className={`px-4 py-3 border-b flex justify-between items-center ${hasIssues(scope) ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                            <div className="flex flex-col">
                                <span className="font-bold text-slate-900">Scope: {scope.scope_name}</span>
                                {/* Show prices if available */}
                                {(scope.proposal_price || scope.contract_price) && (
                                    <div className="flex gap-4 text-xs mt-1">
                                        {scope.proposal_price && (
                                            <span className="text-green-700">Proposal: {scope.proposal_price}</span>
                                        )}
                                        {scope.contract_price && (
                                            <span className="text-blue-700">Contract: {scope.contract_price}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-bold ${hasIssues(scope) ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}>
                                {scope.overall_status || (hasIssues(scope) ? "Not Compliant" : "Compliant")}
                            </span>
                        </div>
                        
                        {/* Section Status Grid */}
                        {scope.sections && (
                            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(scope.sections).map(([key, section]) => (
                                        <div key={key} className={`px-3 py-1.5 rounded-sm text-xs font-semibold flex items-center gap-2 ${
                                            section.status === "Compliant" ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                            <div className={`w-2 h-2 rounded-full ${getSectionStatusBg(section.status)}`}></div>
                                            {sectionLabels[key] || key}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Discrepancies */}
                        {scope.discrepancies && scope.discrepancies.length > 0 && (
                            <div className="p-4 space-y-4">
                                <div className="text-sm font-bold text-red-700 mb-2">⚠️ Discrepancies Found ({scope.discrepancies.length})</div>
                                
                                {scope.discrepancies.map((disc, dIdx) => (
                                    <div key={dIdx} className="bg-red-50 p-4 rounded-sm border border-red-200 space-y-3">
                                        {/* Section Badge */}
                                        <div className="flex items-center gap-2">
                                            <span className="px-2 py-1 rounded text-xs font-bold bg-red-600 text-white">
                                                {disc.section}
                                            </span>
                                        </div>
                                        
                                        {/* Proposal Reference */}
                                        {disc.proposal_reference && (
                                            <div>
                                                <div className="text-xs text-green-600 uppercase tracking-wide font-semibold mb-1">Proposal Reference:</div>
                                                <div className="bg-green-50 p-2 rounded border border-green-200 text-sm text-slate-700 italic">
                                                    "{disc.proposal_reference}"
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Contract Reference */}
                                        {disc.contract_reference && (
                                            <div>
                                                <div className="text-xs text-blue-600 uppercase tracking-wide font-semibold mb-1">Contract Reference:</div>
                                                <div className="bg-blue-50 p-2 rounded border border-blue-200 text-sm text-slate-700 italic">
                                                    "{disc.contract_reference}"
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Comments */}
                                        {disc.comments && (
                                            <div>
                                                <div className="text-xs text-red-600 uppercase tracking-wide font-semibold mb-1">Comments:</div>
                                                <div className="bg-white p-2 rounded border border-red-200 text-sm text-slate-800">
                                                    {disc.comments}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* No Discrepancies - All Green */}
                        {(!scope.discrepancies || scope.discrepancies.length === 0) && !hasIssues(scope) && (
                            <div className="p-4 text-center text-green-700">
                                <div className="text-sm font-medium">✓ All sections compliant - No action required</div>
                            </div>
                        )}
                    </Card>
                ))}
            </div>

            {/* No Scopes Message */}
            {(!data.scopes_identified || data.scopes_identified.length === 0) && (
                <div className="text-center py-8 text-slate-500">
                    <p>No scopes identified yet.</p>
                    <p className="text-sm mt-2">Upload a Proposal document to begin scope review.</p>
                </div>
            )}

            {/* Final Status */}
            <div className={`p-4 rounded-sm ${getStatusColor(data.scope_review_status)}`}>
                <div className="font-bold text-center">
                    {data.scope_review_status || "Scope Review Status: Pending"}
                </div>
            </div>
        </div>
    );
};

// History View Component - View and load past contract reviews
const HistoryView = ({ reviews, onLoadReview, onDeleteReview, onRefresh, isLoading }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric',
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getRetentionColor = (daysRemaining) => {
        if (daysRemaining === null || daysRemaining === undefined) return "text-slate-400";
        if (daysRemaining <= 7) return "text-red-600";
        if (daysRemaining <= 30) return "text-yellow-600";
        return "text-green-600";
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">Contract Review History</h2>
                    <p className="text-sm text-slate-500 mt-1">Reviews are retained for 90 days</p>
                </div>
                <button 
                    onClick={onRefresh}
                    disabled={isLoading}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                >
                    <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>

            {reviews.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                    <Archive size={48} className="mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No contract reviews in history</p>
                    <p className="text-sm mt-2">Completed reviews will appear here</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {reviews.map((review) => (
                        <Card key={review.review_id} className="overflow-hidden hover: transition-shadow">
                            <div className="p-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-slate-900 truncate">
                                            {review.project_name || "Untitled Review"}
                                        </h3>
                                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                                            <span className="flex items-center gap-1">
                                                <Calendar size={12} />
                                                {formatDate(review.updated_at || review.created_at)}
                                            </span>
                                            <span className={`flex items-center gap-1 ${getRetentionColor(review.days_remaining)}`}>
                                                <Clock size={12} />
                                                {review.days_remaining !== null ? `${review.days_remaining} days left` : ""}
                                            </span>
                                            {review.saved_by && (
                                                <span className="flex items-center gap-1" data-testid="review-saved-by">
                                                    <User size={12} />
                                                    Saved by {review.saved_by}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 ml-4">
                                        <button
                                            type="button"
                                            data-testid={`ca-load-review-${review.review_id}`}
                                            onClick={() => onLoadReview(review.review_id)}
                                            className="px-3 py-1.5 text-xs font-medium rounded transition-colors border border-slate-300 bg-transparent text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800"
                                        >
                                            Load
                                        </button>
                                        <button
                                            type="button"
                                            data-testid={`ca-delete-review-${review.review_id}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                console.log("Delete clicked for:", review.review_id);
                                                onDeleteReview(review.review_id);
                                            }}
                                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                            title="Delete review"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Status indicators */}
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {review.contract_count > 0 && (
                                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                            {review.contract_count} Contract{review.contract_count > 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {review.proposal_count > 0 && (
                                        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                            {review.proposal_count} Proposal{review.proposal_count > 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {review.has_summary && (
                                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                                            Summary
                                        </span>
                                    )}
                                    {review.has_terms && (
                                        <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                                            Terms
                                        </span>
                                    )}
                                    {review.has_scope && (
                                        <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                                            Scope
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

// Exclusions View Component - Lists all exclusions from the proposal
const ExclusionsView = ({ exclusionsData, onCopyToEmail }) => {
    const [copied, setCopied] = useState(false);
    
    // Group exclusions by scope
    const groupedExclusions = exclusionsData?.exclusions_by_scope || [];
    const generalExclusions = exclusionsData?.general_exclusions || [];
    
    // Generate email-formatted content for exclusions
    const generateExclusionsEmailContent = () => {
        let content = [];
        
        content.push('• PROPOSAL EXCLUSIONS');
        content.push('');
        
        // General exclusions first
        if (generalExclusions.length > 0) {
            content.push('    • GENERAL EXCLUSIONS:');
            generalExclusions.forEach((exc) => {
                content.push(`        - ${exc}`);
            });
            content.push('');
        }
        
        // Scope-specific exclusions
        groupedExclusions.forEach((scopeGroup) => {
            if (scopeGroup.exclusions && scopeGroup.exclusions.length > 0) {
                content.push(`    • ${scopeGroup.scope_name.toUpperCase()}:`);
                scopeGroup.exclusions.forEach((exc) => {
                    content.push(`        - ${exc}`);
                });
                content.push('');
            }
        });
        
        if (groupedExclusions.length === 0 && generalExclusions.length === 0) {
            content.push('    No exclusions found in proposal.');
        }
        
        return content.join('\n');
    };
    
    const copyToClipboard = async () => {
        try {
            const content = generateExclusionsEmailContent();
            await navigator.clipboard.writeText(content);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };
    
    const copyToEmailTab = () => {
        if (onCopyToEmail) {
            onCopyToEmail(generateExclusionsEmailContent());
        }
    };
    
    const totalExclusions = generalExclusions.length + 
        groupedExclusions.reduce((sum, g) => sum + (g.exclusions?.length || 0), 0);
    
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                        <FileText size={20} />
                        Proposal Exclusions
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        All exclusions extracted from the proposal document
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={copyToClipboard}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                            copied 
                                ? 'bg-green-600 text-white' 
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                        {copied ? (
                            <>
                                <CheckCircle size={16} />
                                Copied!
                            </>
                        ) : (
                            <>
                                <Copy size={16} />
                                Copy All
                            </>
                        )}
                    </button>
                </div>
            </div>
            
            {/* Stats */}
            <div className="bg-slate-50 p-4 rounded-sm">
                <div className="flex items-center gap-4 text-sm">
                    <span className="text-slate-500">Total Exclusions:</span>
                    <span className="font-bold text-slate-900">{totalExclusions}</span>
                    <span className="text-slate-300">|</span>
                    <span className="text-slate-500">Scopes with Exclusions:</span>
                    <span className="font-bold text-slate-900">
                        {groupedExclusions.filter(g => g.exclusions?.length > 0).length}
                    </span>
                </div>
            </div>
            
            {/* General Exclusions */}
            {generalExclusions.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4">
                    <h3 className="font-bold text-yellow-800 mb-3 flex items-center gap-2">
                        <AlertTriangle size={16} />
                        General Exclusions
                    </h3>
                    <ul className="space-y-2">
                        {generalExclusions.map((exc, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                                <span className="text-yellow-600 mt-1">•</span>
                                <span>{exc}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            
            {/* Scope-Specific Exclusions */}
            {groupedExclusions.length > 0 && (
                <div className="space-y-4">
                    <h3 className="font-bold text-slate-800">Scope-Specific Exclusions</h3>
                    {groupedExclusions.map((scopeGroup, idx) => (
                        <div key={idx} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                            <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
                                <span className="font-semibold text-slate-800">{scopeGroup.scope_name}</span>
                                <span className="ml-2 text-sm text-slate-500">
                                    ({scopeGroup.exclusions?.length || 0} exclusions)
                                </span>
                            </div>
                            {scopeGroup.exclusions && scopeGroup.exclusions.length > 0 ? (
                                <ul className="p-4 space-y-2">
                                    {scopeGroup.exclusions.map((exc, excIdx) => (
                                        <li key={excIdx} className="flex items-start gap-2 text-sm text-slate-700">
                                            <span className="text-red-500 mt-1">•</span>
                                            <span>{exc}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="p-4 text-sm text-slate-400 italic">
                                    No specific exclusions for this scope
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            
            {/* No Exclusions Message */}
            {totalExclusions === 0 && (
                <div className="text-center py-8 text-slate-400">
                    <FileText size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No exclusions found in the proposal.</p>
                    <p className="text-sm mt-2">Upload a proposal document to extract exclusions.</p>
                </div>
            )}
        </div>
    );
};

// Email View Component - Combines Terms and Exclusions for easy copy/paste to email
const EmailView = ({ termsData, exclusionsData, projectName, isNewClient }) => {
    const [copied, setCopied] = useState(false);
    
    // Generate email-formatted content
    const generateEmailContent = () => {
        let content = [];
        
        // Standard intro paragraph
        content.push('Thank you for partnering with ABS. We look forward to a successful project together.');
        content.push('');
        content.push('We have completed our review of the contract documents and identified several items requiring clarification prior to execution. To streamline review, our comments are organized by topic below.');
        content.push('');
        content.push('');
        
        // Project header (no bullet on main section)
        if (projectName) {
            content.push(`𝗣𝗥𝗢𝗝𝗘𝗖𝗧: ${projectName}`);
            content.push('');
        }
        
        // TERMS SECTION (no bullet on main section)
        if (termsData && termsData.length > 0 || isNewClient) {
            content.push('𝗧𝗘𝗥𝗠𝗦');
            content.push('');
            content.push('The following terms reflect clarifications included in our proposal. We request confirmation prior to execution.');
            content.push('');
            
            // Regular terms items - shortened format
            if (termsData && termsData.length > 0) {
                termsData.forEach((item) => {
                    // Skip Post-Award Deliverables from email
                    if (item.title?.toLowerCase().includes('post-award') || 
                        item.title?.toLowerCase().includes('post award')) {
                        return;
                    }
                    
                    // Title with contract section inline
                    const sectionRef = item.contract_section || item.clause_reference || '';
                    const titleLine = sectionRef ? `${item.title}: ${sectionRef}` : item.title;
                    content.push(titleLine);
                    
                    // Response (the main statement/request)
                    const responseText = item.clarification || item.proposal_text || '';
                    if (responseText) {
                        content.push(`• Response: ${responseText}`);
                    }
                    
                    // Comment - skip for offsite storage, prime contract/agreement, material escalation
                    const skipComment = item.title?.toLowerCase().includes('offsite storage') ||
                                       item.title?.toLowerCase().includes('prime') ||
                                       item.title?.toLowerCase().includes('material escalation');
                    
                    const commentText = item.comment || item.reason;
                    if (commentText && !skipComment) {
                        content.push(`• Comment: ${commentText}`);
                    }
                    content.push('');
                });
            }
            
            // New Client Retainer at bottom of TERMS (if checkbox is checked)
            if (isNewClient) {
                content.push('New Client Retainer');
                content.push('• Response: A new client retainer applies and will be included in the Schedule of Values.');
                content.push('');
            }
        }
        
        // EXCLUSIONS SECTION (no bullet on main section, always at the end)
        if (exclusionsData) {
            const generalExclusions = exclusionsData.general_exclusions || [];
            const scopeExclusions = exclusionsData.exclusions_by_scope || [];
            const totalExclusions = generalExclusions.length + 
                scopeExclusions.reduce((sum, g) => sum + (g.exclusions?.length || 0), 0);
            
            if (totalExclusions > 0) {
                content.push('𝗣𝗥𝗢𝗣𝗢𝗦𝗔𝗟 𝗘𝗫𝗖𝗟𝗨𝗦𝗜𝗢𝗡𝗦');
                content.push('');
                content.push('The following exclusions were included in our original proposal and may not be fully reflected in the contract documents. We request confirmation that these items are incorporated prior to execution.');
                content.push('');
                
                // General exclusions first
                if (generalExclusions.length > 0) {
                    content.push('General Exclusions');
                    generalExclusions.forEach((exc) => {
                        content.push(`• ${exc}`);
                    });
                    content.push('');
                }
                
                // Scope-specific exclusions
                scopeExclusions.forEach((scopeGroup) => {
                    if (scopeGroup.exclusions && scopeGroup.exclusions.length > 0) {
                        content.push(scopeGroup.scope_name);
                        scopeGroup.exclusions.forEach((exc) => {
                            content.push(`• ${exc}`);
                        });
                        content.push('');
                    }
                });
                
                // Confirmation request at end of exclusions
                content.push('Please reply confirming you acknowledge and agree to the exclusions outlined.');
                content.push('');
            }
        }
        
        // No data message
        if ((!termsData || termsData.length === 0) && !exclusionsData) {
            content.push('No Terms or Exclusions data available.');
            content.push('Please run a Contract Review and/or upload a Proposal first.');
        }
        
        return content.join('\n');
    };
    
    const emailContent = generateEmailContent();
    
    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(emailContent);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = emailContent;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };
    
    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                        <Mail size={20} />
                        Email Export
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Copy Terms and Exclusions to paste into your email
                    </p>
                </div>
                <button
                    onClick={copyToClipboard}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                        copied 
                            ? 'bg-green-600 text-white' 
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                    {copied ? (
                        <>
                            <CheckCircle size={16} />
                            Copied!
                        </>
                    ) : (
                        <>
                            <Copy size={16} />
                            Copy to Clipboard
                        </>
                    )}
                </button>
            </div>
            
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-1">
                <div className="bg-white rounded border border-slate-100 p-6 overflow-auto max-h-[calc(100vh-280px)]">
                    <pre 
                        className="text-slate-800"
                        style={{ 
                            fontFamily: 'Aptos, Calibri, Arial, sans-serif',
                            fontSize: '11pt',
                            lineHeight: '1.5',
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            margin: 0
                        }}
                    >
                        {emailContent}
                    </pre>
                </div>
            </div>
            
            <div className="text-xs text-slate-400 text-center">
                Font: Aptos, 11pt • Headers formatted as bullets • Ready for email paste
            </div>
        </div>
    );
};

// --- Main App ---

export default function ContractAdminApp() {
  const { user } = useAuth(); // available to the ported code; host session drives it
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSidebarOpen, setSidebarOpen] = useState(true); // kept for legacy refs; panel is always shown
  const [activeTab, setActiveTab] = useUrlState("tab", "chat");
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [taskType, setTaskType] = useState("CONTRACT_REVIEW");
  const [isNewClient, setIsNewClient] = useState(false);  // New Client checkbox state
  
  // State for document management - filtered by current session
  const [contracts, setContracts] = useState([]);  // Contract documents for current session
  const [proposals, setProposals] = useState([]);  // Proposal documents for current session
  const [activeContract, setActiveContract] = useState(null);
  const [activeProposal, setActiveProposal] = useState(null);
  
  // History state
  const [reviewHistory, setReviewHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [loadedReviewId, setLoadedReviewId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const workspaceRef = useRef(null);
  const [search, setSearch] = useState("");
  const [matchCounts, setMatchCounts] = useState({});
  const reviewSearchRef = useRef(false);
  const skipDocsLoadRef = useRef(false);
  const [viewer, setViewer] = useState(null);   // document open in the side panel
  const [paneClosed, setPaneClosed] = useState(false);   // user closed it → stays closed until View
  const [paneWidth, setPaneWidth] = useState(75);
  const openViewer = (doc, type) => { setPaneClosed(false); setViewer({ ...doc, type }); };
  const paneDocs = { contract: currentOf(contracts), proposal: currentOf(proposals) };
  // Terms opens the contract; Scope / Exclusions open the proposal (other tabs leave the pane as it is).
  useEffect(() => {
    if (paneClosed) return;
    const want = activeTab === "terms" ? "contract" : ["scope", "exclusions"].includes(activeTab) ? "proposal" : null;
    const doc = want && (paneDocs[want] || paneDocs.contract);
    if (doc && viewer?.file_id !== doc.file_id) setViewer({ ...doc, type: doc === paneDocs[want] ? want : "contract" });
  }, [activeTab, contracts, proposals, paneClosed]); // eslint-disable-line react-hooks/exhaustive-deps
  // Jump to Clause — open the contract on the page holding a flagged term's language.
  const jumpToClause = async (item) => {
    const doc = paneDocs.contract;
    if (!doc) { toast.error("Upload the contract first"); return; }
    if (!doc.filename?.toLowerCase().endsWith(".pdf")) { openViewer(doc, "contract"); toast("Page jump works for PDF contracts"); return; }
    const qs = new URLSearchParams({ q: (item.verbatim_text || "").slice(0, 600), section: item.contract_section || item.clause_reference || "" });
    try {
      const r = await authed(`/api/documents/${doc.file_id}/locate?${qs}`);
      const { page } = await r.json();
      setPaneClosed(false);
      setViewer({ ...doc, type: "contract", page: page || undefined, jump: Date.now() });
      if (!page) toast("Couldn't find that clause in the contract");
    } catch { toast.error("Couldn't search the contract"); }
  };
  const removeDoc = (id, type) => { if (viewer?.file_id === id) setViewer(null); deleteDocument(id, type); };
  const backendUrl = ""; // Use relative path for Ingress routing

  // Load documents for current session and history on mount
  useEffect(() => {
    createNewSession(true);
  }, []);

  // Load documents when sessionId changes
  useEffect(() => {
    // A history load brings its own documents — don't overwrite them with the session's.
    if (skipDocsLoadRef.current) { skipDocsLoadRef.current = false; return; }
    if (sessionId) {
      loadDocuments(sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load history on mount
  useEffect(() => {
    loadHistory();
  }, []);

  const loadDocuments = async (sid) => {
    try {
      const url = sid ? `${backendUrl}/api/documents?session_id=${sid}` : `${backendUrl}/api/documents`;
      const res = await fetch(url);
      const docs = await res.json();
      
      const contractDocs = docs.filter(d => d.document_type === 'contract');
      const proposalDocs = docs.filter(d => d.document_type === 'proposal');
      
      setContracts(contractDocs);
      setProposals(proposalDocs);
      
      // Set active documents
      const activeC = contractDocs.find(d => d.is_active);
      const activeP = proposalDocs.find(d => d.is_active);
      setActiveContract(activeC || null);
      setActiveProposal(activeP || null);
    } catch (err) {
      console.error("Failed to load documents", err);
    }
  };

  const createNewSession = async (reuseInFlight = false) => {
    try {
      // Remounting the tab (or React's dev double-mount) must not spawn extra
      // sessions: the first mount's request is reused. "Save & New Review" passes
      // reuseInFlight = false and always gets a fresh session.
      if (reuseInFlight && openSessionRequest) {
        const existing = await openSessionRequest;
        setSessionId(existing.session_id);
      } else {
        openSessionRequest = fetch(`${backendUrl}/api/sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ task_type: taskType })
        }).then(r => r.json());
        const data = await openSessionRequest;
        setSessionId(data.session_id);
      }
      setMessages([{ role: 'assistant', content: `Greetings and salutations, I am your Contract Administrator. Upload your contract and proposal to begin review.` }]);
      setAnalysisResult(null);
      setLoadedReviewId(null);
      // Clear documents for new session
      setContracts([]);
      setProposals([]);
      setActiveContract(null);
      setActiveProposal(null);
      // Reset processing states
      setIsProcessing(false);
      setProcessingQueue([]);
      setProcessingStats({ completed: 0, total: 0 });
      setProcessingStatus({ stage: '', filename: '', progress: 0, message: '' });
    } catch (err) {
      console.error("Failed to create session", err);
    }
  };

  // Extract shortened project name from contract filename (number and name only)
  const extractProjectName = (filename) => {
    if (!filename) return null;
    // Remove file extension
    let name = filename.replace(/\.(pdf|docx|doc)$/i, '');
    // Try to extract project number and name (e.g., "18426 Grand County EMS" from "18426 Grand County EMS - ABS Subcontract - Received.pdf")
    const match = name.match(/^(\d+)\s+([^-–]+)/);
    if (match) {
      return `${match[1]} ${match[2].trim()}`;
    }
    // If no number found, just return first part before dash
    const firstPart = name.split(/[-–]/)[0].trim();
    return firstPart || name;
  };

  // History functions
  const loadHistory = async () => {
    setIsHistoryLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/contract-reviews?limit=50`);
      const data = await res.json();
      setReviewHistory(data.reviews || []);
    } catch (err) {
      console.error("Failed to load history", err);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const saveCurrentReview = async () => {
    if (!sessionId) return;
    
    // Auto-generate project name from contract filename
    const autoProjectName = activeContract ? extractProjectName(activeContract.filename) : null;
    const projectName = autoProjectName || analysisResult?.structured_data?.summary_data?.project_name || 'Untitled Review';
    
    try {
      const res = await fetch(`${backendUrl}/api/contract-reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          project_name: projectName,
          messages: messages,
          summary_data: analysisResult?.structured_data?.summary_data,
          negotiation_summary: analysisResult?.structured_data?.negotiation_summary,
          scope_data: analysisResult?.structured_data?.scope_data,
          analysis_result: analysisResult,
          task_type: taskType
        })
      });
      const data = await res.json();
      console.log("Review saved:", data);
      // Refresh history
      loadHistory();
      return data;
    } catch (err) {
      console.error("Failed to save review", err);
    }
  };

  // Start a fresh review (current one is already auto-saved). Triggered by uploading a new contract.
  const startNewReview = async () => {
    // Only save if there's something to save (has analysis or documents)
    if (analysisResult || activeContract || activeProposal) {
      await saveCurrentReview();
    }
    
    // Clear active documents in the database
    try {
      await fetch(`${backendUrl}/api/documents/clear-active`, {
        method: 'POST'
      });
    } catch (err) {
      console.error("Failed to clear documents", err);
    }
    
    // Reset all processing states
    setIsProcessing(false);
    setProcessingQueue([]);
    setProcessingStats({ completed: 0, total: 0 });
    setProcessingStatus({ 
      stage: '', 
      filename: '',
      progress: 0,
      message: ''
    });
    
    // Reset file inputs so they can accept new files
    const fileInputs = document.querySelectorAll('input[type="file"]');
    fileInputs.forEach(input => {
      input.value = '';
    });
    
    // Create new session
    await createNewSession();
  };

  const loadReviewFromHistory = async (reviewId) => {
    try {
      const res = await fetch(`${backendUrl}/api/contract-reviews/${reviewId}`);
      const review = await res.json();
      
      // Load the review data into state; continue in its own session so auto-save updates this review
      setLoadedReviewId(reviewId);
      if (review.session_id && review.session_id !== sessionId) { skipDocsLoadRef.current = true; setSessionId(review.session_id); }
      // Auto-save stores the "Loaded…" notice too — drop old ones so they don't stack up on every open
      setMessages((review.messages || []).filter((m) => !String(m.content || '').startsWith('Loaded contract review:')));
      
      // Reconstruct analysis result
      if (review.summary_data || review.negotiation_summary || review.scope_data) {
        setAnalysisResult({
          structured_data: {
            summary_data: review.summary_data,
            negotiation_summary: review.negotiation_summary,
            scope_data: review.scope_data,
            exclusions_data: review.analysis_result?.structured_data?.exclusions_data
          },
          ...review.analysis_result
        });
      }
      
      // Set task type if available
      if (review.task_type) {
        setTaskType(review.task_type);
      }
      
      // Load documents associated with the review
      setContracts(review.contracts || []);
      setProposals(review.proposals || []);
      
      // Set active documents
      const activeC = (review.contracts || []).find(d => d.is_active);
      const activeP = (review.proposals || []).find(d => d.is_active);
      setActiveContract(activeC || null);
      setActiveProposal(activeP || null);
      
      // Switch to chat tab
      setActiveTab('chat');
      
      // Show notification
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Loaded contract review: **${review.project_name || 'Untitled'}** (${review.days_remaining} days remaining)` 
      }]);
      
    } catch (err) {
      console.error("Failed to load review", err);
    }
  };

  const deleteReviewFromHistory = async (reviewId) => {
    // Confirm deletion
    if (!window.confirm("Delete this contract review?")) {
      return;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/contract-reviews/${reviewId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`Delete failed: ${response.status}`);
      }
      
      // Refresh history after successful delete
      await loadHistory();
    } catch (err) {
      console.error("Failed to delete review", err);
      alert("Failed to delete review. Please try again.");
    }
  };

  // Reviews save themselves: after analysis lands and after every chat turn (debounced), keyed by session.
  useEffect(() => {
    if (!sessionId || !(analysisResult || messages.some((m) => m.role === 'user'))) return;
    const t = setTimeout(saveCurrentReview, 1500);
    return () => clearTimeout(t);
  }, [analysisResult, messages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search inside the open review: count per tab and highlight in place.
  useEffect(() => {
    if (!reviewSearchRef.current) { paintMatches(null, ""); setMatchCounts({}); return; }
    const t = setTimeout(() => setMatchCounts(paintMatches(workspaceRef.current, search.trim())), 150);
    return () => clearTimeout(t);
  }, [search, activeTab, messages, analysisResult]);
  useEffect(() => () => paintMatches(null, ""), []);

  const [processingQueue, setProcessingQueue] = useState([]);
  const [processingStats, setProcessingStats] = useState({ completed: 0, total: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState({ 
    stage: '', // 'uploading', 'extracting', 'analyzing', 'complete', 'error'
    filename: '',
    progress: 0,
    message: ''
  });

  useEffect(() => {
    if (processingQueue.length > 0 && !isProcessing) {
        processNextInQueue();
    }
  }, [processingQueue, isProcessing]);

  const processNextInQueue = async () => {
      setIsProcessing(true);
      const currentFile = processingQueue[0];
      const { file, taskType, documentType } = currentFile;

      // Update processing status (left pane only)
      setProcessingStatus({
        stage: 'uploading',
        filename: file.name,
        progress: 10,
        message: `Uploading ${file.name}...`
      });

      try {
          // 1. Upload with document type and session_id
          setProcessingStatus(prev => ({ ...prev, stage: 'uploading', progress: 20, message: `Uploading ${file.name}...` }));
          
          const formData = new FormData();
          formData.append('file', file);
          
          const uploadUrl = sessionId 
            ? `${backendUrl}/api/upload?document_type=${documentType}&session_id=${sessionId}`
            : `${backendUrl}/api/upload?document_type=${documentType}`;
          
          const uploadRes = await fetch(uploadUrl, {
              method: 'POST',
              body: formData
          });
          
          if (!uploadRes.ok) {
              const err = await uploadRes.json().catch(() => ({}));
              throw new Error(err.detail || `Upload failed: ${uploadRes.status}`);
          }
          
          setProcessingStatus(prev => ({ ...prev, stage: 'extracting', progress: 40, message: `Extracting text from ${file.name}...` }));
          
          const uploadData = await uploadRes.json();
          
          // Reload documents for current session
          await loadDocuments(sessionId);
          
          setProcessingStatus(prev => ({ ...prev, progress: 50, message: `Document uploaded successfully` }));

          // 2. Attach to session
          if (sessionId) {
              await fetch(`${backendUrl}/api/sessions/${sessionId}/attach`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ file_id: uploadData.file_id })
              });

              // 3. Analyze ONLY for contracts, NOT for proposals
              // Proposals are only used for the Scope tab comparison
              if (documentType === "contract") {
                  setProcessingStatus(prev => ({ ...prev, stage: 'analyzing', progress: 60, message: `Analyzing ${file.name}...` }));
                  await runAnalysis(uploadData.file_id, taskType, file.name);
                  setProcessingStatus(prev => ({ ...prev, stage: 'complete', progress: 100, message: `Completed ${file.name}` }));
              } else {
                  // Proposal uploaded - ALWAYS extract exclusions first
                  setProcessingStatus(prev => ({ ...prev, stage: 'analyzing', progress: 60, message: `Extracting exclusions from ${file.name}...` }));
                  
                  // Run PROPOSAL_EXCLUSIONS to extract exclusions immediately
                  try {
                      const exclusionsRes = await fetch(`${backendUrl}/api/analyze`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ file_id: uploadData.file_id, task_type: "PROPOSAL_EXCLUSIONS" })
                      });
                      
                      if (exclusionsRes.ok) {
                          const exclusionsResult = await exclusionsRes.json();
                          // Merge exclusions_data into analysis result
                          setAnalysisResult(prev => ({
                              ...prev,
                              structured_data: {
                                  ...prev?.structured_data,
                                  exclusions_data: exclusionsResult?.structured_data?.exclusions_data
                              }
                          }));
                          
                          const totalExclusions = (exclusionsResult?.structured_data?.exclusions_data?.general_exclusions?.length || 0) +
                              (exclusionsResult?.structured_data?.exclusions_data?.exclusions_by_scope?.reduce((sum, g) => sum + (g.exclusions?.length || 0), 0) || 0);
                          
                          setMessages(prev => [...prev, { 
                            role: 'assistant', 
                            content: `**Proposal uploaded:** ${file.name}\n\n**${totalExclusions} exclusions extracted.** See the Exclusions tab for details.` 
                          }]);
                      }
                  } catch (exclusionsErr) {
                      console.error("Exclusions extraction error:", exclusionsErr);
                  }
                  
                  // Then check if contract exists and auto-run scope review
                  if (activeContract) {
                      // Contract exists - auto-run scope review
                      setProcessingStatus(prev => ({ ...prev, stage: 'analyzing', progress: 80, message: `Running Scope Review...` }));
                      setMessages(prev => [...prev, { 
                        role: 'assistant', 
                        content: `Contract detected. Automatically running Scope Review...` 
                      }]);
                      
                      // Run scope review with the proposal
                      try {
                          const scopeRes = await fetch(`${backendUrl}/api/analyze`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ file_id: uploadData.file_id, task_type: "SCOPE_REVIEW" })
                          });
                          
                          if (scopeRes.ok) {
                              const scopeResult = await scopeRes.json();
                              // MERGE scope data with existing analysis result - DO NOT replace summary/terms/exclusions
                              setAnalysisResult(prev => ({
                                  ...prev,
                                  structured_data: {
                                      ...prev?.structured_data,
                                      scope_data: scopeResult?.structured_data?.scope_data || scopeResult?.structured_data
                                  }
                              }));
                              setActiveTab('scope');
                              const scopeStatus = scopeResult?.structured_data?.scope_data?.scope_review_status || 
                                                  scopeResult?.structured_data?.scope_review_status || "Review Complete";
                              setMessages(prev => [...prev, { 
                                role: 'assistant', 
                                content: `**Scope Review Complete**\n\nStatus: ${scopeStatus}\n\nSee the Scope tab for details.` 
                              }]);
                          }
                      } catch (scopeErr) {
                          console.error("Auto scope review error:", scopeErr);
                      }
                      
                      setProcessingStatus(prev => ({ ...prev, stage: 'complete', progress: 100, message: `Scope Review Complete` }));
                  } else {
                      // No contract yet - just show exclusions extracted
                      setProcessingStatus(prev => ({ ...prev, stage: 'complete', progress: 100, message: `Exclusions extracted from ${file.name}` }));
                      setActiveTab('exclusions');
                  }
              }
          }

      } catch (err) {
          console.error(`Error processing ${file.name}`, err);
          setProcessingStatus(prev => ({ ...prev, stage: 'error', progress: 0, message: err.message || 'Processing failed' }));
          // Only show error in chat
          setMessages(prev => [...prev, { role: 'assistant', content: `Error processing **${file.name}**: ${err.message || 'Please try again.'}` }]);
      } finally {
          // Cleanup
          setProcessingStats(prev => ({ ...prev, completed: prev.completed + 1 }));
          setProcessingQueue(prev => prev.slice(1));
          setIsProcessing(false);
          
          // Clear status after a delay if queue is empty
          if (processingQueue.length <= 1) {
            setTimeout(() => {
              setProcessingStatus({ stage: '', filename: '', progress: 0, message: '' });
            }, 2000);
          }
      }
  };

  // Handle Contract upload. A new contract = a new contract review: if this review already has a
  // contract (or is a loaded history review), save it and start fresh before queueing the file.
  const handleContractUpload = async (e, { replace = false } = {}) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    if (!replace && (contracts.length > 0 || loadedReviewId)) {
      await startNewReview();
      setMessages((m) => [...m, { role: 'assistant', content: 'New contract uploaded: started a new contract review (the previous one is saved in History).' }]);
    }

    // Reset stats if starting a new batch from idle
    if (processingQueue.length === 0) {
        setProcessingStats({ completed: 0, total: selectedFiles.length });
    } else {
        setProcessingStats(prev => ({ ...prev, total: prev.total + selectedFiles.length }));
    }

    // Add to queue with document_type = "contract"
    const newQueueItems = selectedFiles.map(file => ({
        file,
        taskType,
        documentType: "contract"
    }));
    
    setProcessingQueue(prev => [...prev, ...newQueueItems]);
  };

  // Handle Proposal upload (from "Upload Proposal" control)
  const handleProposalUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    // Reset stats if starting a new batch from idle
    if (processingQueue.length === 0) {
        setProcessingStats({ completed: 0, total: selectedFiles.length });
    } else {
        setProcessingStats(prev => ({ ...prev, total: prev.total + selectedFiles.length }));
    }

    // Add to queue with document_type = "proposal"
    const newQueueItems = selectedFiles.map(file => ({
        file,
        taskType,
        documentType: "proposal"
    }));
    
    setProcessingQueue(prev => [...prev, ...newQueueItems]);
  };

  // Set a document as active
  const setDocumentActive = async (fileId, documentType) => {
    try {
      await fetch(`${backendUrl}/api/documents/set-active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_id: fileId, document_type: documentType })
      });
      await loadDocuments(sessionId);
      setMessages(prev => [...prev, { 
        role: 'system', 
        content: `Set ${documentType} as active.` 
      }]);
    } catch (err) {
      console.error("Failed to set active document", err);
    }
  };

  // Delete a document
  const deleteDocument = async (fileId, documentType) => {
    try {
      await fetch(`${backendUrl}/api/documents/${fileId}`, {
        method: 'DELETE'
      });
      await loadDocuments(sessionId);
      setMessages(prev => [...prev, { 
        role: 'system', 
        content: `Deleted ${documentType} document.` 
      }]);
    } catch (err) {
      console.error("Failed to delete document", err);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const runAnalysis = async (fileId, type, filename, retries = 3) => {
    // Queue manages loading state - no direct setIsLoading call needed here
    
    for (let attempt = 0; attempt < retries; attempt++) {
        try {
            const payload = { file_id: fileId, task_type: type };

            const res = await fetch(`${backendUrl}/api/analyze`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            if (!res.ok) {
                // If it's a 402 (Payment) or 400-499 (Client error), don't retry
                if (res.status === 402 || (res.status >= 400 && res.status < 500)) {
                    const errorData = await res.json();
                    throw new Error(errorData.detail || `Server error: ${res.status}`);
                }
                // For 500s, throw to trigger retry
                const errorText = await res.text();
                throw new Error(`Server error ${res.status}: ${errorText}`);
            }
            
            const result = await res.json();
            
            // Check if schedule was extracted
            let scheduleMsg = "";
            if (result.schedule_pdf) {
                scheduleMsg = `\n\nI extracted the **Project Schedule** for ${filename} into a separate PDF.`;
            }
            
            // Add summary message
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                content: `**${filename}**: Analysis Complete!${scheduleMsg} Open the Summary, Terms and Scope tabs for details.` 
            }]);
            
            // Update result view only if it's the latest
            setAnalysisResult(result);
            setActiveTab('summary'); // Default to Summary tab
            return; // Success, exit loop
            
        } catch (err) {
            console.warn(`Analysis attempt ${attempt + 1} failed:`, err);
            // If it's the last attempt or a non-retriable error (handled above), rethrow
            if (attempt === retries - 1 || err.message.includes("Payment Required") || err.message.includes("Quota Exceeded")) {
                throw err;
            }
            // Wait before retry (exponential backoff)
            await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
        }
    }
  };

  // Run Scope Review specifically - triggered from Scope tab
  const runScopeReview = async () => {
    if (!activeProposal) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `**Scope Review:** Please upload a Proposal document first. The Proposal defines what ABS priced and is required for scope review.` 
      }]);
      return;
    }

    setIsLoading(true);
    setActiveTab('scope');
    
    try {
      const payload = { 
        file_id: activeProposal.file_id, 
        task_type: "SCOPE_REVIEW" 
      };

      const res = await fetch(`${backendUrl}/api/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error: ${res.status}`);
      }
      
      const result = await res.json();
      
      // MERGE scope data AND exclusions_data with existing analysis result - DO NOT replace summary/terms
      setAnalysisResult(prev => ({
          ...prev,
          structured_data: {
              ...prev?.structured_data,
              scope_data: result?.structured_data?.scope_data || result?.structured_data,
              exclusions_data: result?.structured_data?.exclusions_data
          }
      }));
      
      // Add message based on result
      const scopeStatus = result?.structured_data?.scope_data?.scope_review_status || 
                          result?.structured_data?.scope_review_status || 
                          "Review Complete";
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `**Scope Review Complete**\n\nStatus: ${scopeStatus}\n\nSee the Scope tab for details.` 
      }]);
      
    } catch (err) {
      console.error("Scope review error:", err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `**Scope Review Error:** ${err.message || 'Please try again.'}` 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !sessionId) return;
    
    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);

    try {
      const res = await fetch(`${backendUrl}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: userMsg.content })
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I encountered an error." }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Search scope: saved reviews until a review has content, then the open review itself.
  const reviewSearch = !!(analysisResult || loadedReviewId || contracts.length || proposals.length || messages.some((m) => m.role === 'user'));
  const ql = search.trim().toLowerCase();
  const savedMatches = reviewHistory.filter((r) => [r.project_name, r.saved_by, ...(r.contracts || []).map((d) => d.filename), ...(r.proposals || []).map((d) => d.filename)]
    .some((v) => String(v || '').toLowerCase().includes(ql))).slice(0, 20);
  reviewSearchRef.current = reviewSearch;
  const procTone = processingStatus.stage === 'error' ? 'text-red-600' : processingStatus.stage === 'complete' ? 'text-green-600' : 'text-[#007BFF]';
  return (
    <div className="ca-scope flex flex-1 flex-col overflow-hidden font-sans text-slate-900" data-testid="contract-admin-app">
      <SubTabs
        tabs={CA_TABS.map((t) => ({ key: t.id, label: t.label, testId: `ca-tab-${t.id}`,
                                    count: reviewSearch && t.id !== 'history' && matchCounts[t.id] ? matchCounts[t.id] : undefined }))}
        active={activeTab} onChange={setActiveTab} testId="ca-tabs" divider={false}
      />
      <div className="page-pane gap-3">
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="ca-toolbar">
          <div className="relative">
            <SearchBar value={search} onChange={setSearch} testId="ca-search"
                       placeholder="Search..." />
            {!reviewSearch && search.trim() && (
              <ul className="absolute left-0 top-full z-30 mt-1 max-h-72 w-96 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg" data-testid="ca-search-results">
                {savedMatches.map((r) => (
                  <li key={r.review_id}>
                    <button type="button" onClick={() => { setSearch(""); loadReviewFromHistory(r.review_id); }} data-testid={`ca-search-result-${r.review_id}`}
                            className="w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50">
                      <div className="font-medium text-slate-800">{r.project_name || 'Untitled Review'}</div>
                      <div className="text-[10px] text-slate-400">{[r.saved_by, formatDate(r.updated_at || r.created_at)].filter(Boolean).join(' · ')}</div>
                    </button>
                  </li>))}
                {!savedMatches.length && <li className="px-3 py-2 text-xs text-slate-400">No saved reviews match.</li>}
              </ul>)}
          </div>
          {reviewSearch && search.trim() && (
            <span className="text-[11px] text-slate-500" data-testid="ca-search-count">
              {matchCounts[activeTab] || 0} match{(matchCounts[activeTab] || 0) === 1 ? '' : 'es'} here
            </span>)}
          <DocButton label="Contract" type="contract" docs={contracts} onUpload={handleContractUpload}
                     onReplace={(e) => handleContractUpload(e, { replace: true })} onNewReview={handleContractUpload}
                     onView={openViewer} onRemove={removeDoc} onMakeActive={setDocumentActive} />
          <DocButton label="Proposal" type="proposal" docs={proposals} onUpload={handleProposalUpload} onReplace={handleProposalUpload}
                     onView={openViewer} onRemove={removeDoc} onMakeActive={setDocumentActive} />
          {(isProcessing || processingStatus.stage) && (
            <div className={`flex items-center gap-2 text-[11px] ${procTone}`} data-testid="ca-processing-status" title={processingStatus.message}>
              {processingStatus.stage === 'complete' ? <CheckCircle size={13} /> : processingStatus.stage === 'error' ? <AlertTriangle size={13} />
                : <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />}
              <span className="max-w-[16rem] truncate">{processingStatus.message || processingStatus.filename}</span>
              <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-200">
                <div className={`h-1.5 rounded-full transition-all duration-500 ${processingStatus.stage === 'error' ? 'bg-red-500' : processingStatus.stage === 'complete' ? 'bg-green-500' : 'bg-[#007BFF]'}`}
                     style={{ width: `${processingStatus.progress}%` }} />
              </div>
              {processingStats.total > 1 && <span className="text-slate-400">{processingStats.completed + 1}/{processingStats.total}</span>}
            </div>)}
        </div>
        <div className="pane-card border border-slate-200 bg-white">

        {/* Workspace */}
        <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-hidden relative" ref={workspaceRef}>
            
            {/* Chat View */}
            <div className={`absolute inset-0 flex flex-col bg-white transition-opacity duration-200 ${activeTab === 'chat' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`} data-ca-pane="chat">
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-3xl rounded-sm p-4  ${
                                msg.role === 'user' 
                                    ? 'bg-[hsl(var(--brand-blue))] text-white' 
                                    : 'bg-white border border-slate-200 text-slate-800'
                            }`}>
                                <div className="prose prose-sm max-w-none text-[13px] leading-relaxed">
                                    {msg.role === 'system' ? (
                                        <div className="flex items-center gap-2 italic text-slate-500">
                                            <div className="w-4 h-4 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin"></div>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {msg.content}
                                            </ReactMarkdown>
                                        </div>
                                    ) : (
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {msg.content}
                                        </ReactMarkdown>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>
                <div className="p-4 bg-white border-t border-slate-200">
                    <div className="flex gap-2 max-w-3xl">
                        <input 
                            data-testid="ca-chat-input"
                            className="flex-1 border border-slate-300 rounded-sm px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#007BFF]"
                            placeholder="Ask about the contract..."
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && sendMessage()}
                            disabled={isLoading}
                        />
                        <Button variant="data" data-testid="ca-chat-send" onClick={sendMessage} disabled={isLoading}>
                            {isLoading ? '...' : <Play size={16} fill="currentColor" />}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Summary Tab (New) */}
            <div className={`absolute inset-0 overflow-y-auto p-4 bg-white transition-opacity duration-200 ${activeTab === 'summary' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`} data-ca-pane="summary">
                {analysisResult?.structured_data?.summary_data ? (
                    <div className="max-w-4xl mx-auto">
                        <SummaryView data={analysisResult.structured_data.summary_data} />
                    </div>
                ) : (
                    <div className="flex h-full items-center justify-center text-slate-400">
                        No summary available. Run a Contract Review first.
                    </div>
                )}
            </div>

            {/* Negotiations Tab (Terms) */}
            <div className={`absolute inset-0 overflow-y-auto p-4 bg-white transition-opacity duration-200 ${activeTab === 'terms' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`} data-ca-pane="terms">
                {analysisResult?.structured_data?.negotiation_summary ? (
                    <div className="max-w-4xl mx-auto">
                        <NegotiationView 
                            data={analysisResult.structured_data.negotiation_summary}
                            isNewClient={isNewClient}
                            onNewClientChange={setIsNewClient}
                            onJump={jumpToClause}
                        />
                    </div>
                ) : (
                    <div className="flex h-full items-center justify-center text-slate-400">
                        No negotiation items found. Run a Contract Review first.
                    </div>
                )}
            </div>

            {/* Scope Tab */}
            <div className={`absolute inset-0 overflow-y-auto p-4 bg-white transition-opacity duration-200 ${activeTab === 'scope' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`} data-ca-pane="scope">
                {analysisResult?.structured_data?.scope_data ? (
                    <div className="max-w-4xl mx-auto">
                        <div className="flex justify-end mb-4">
                            <Button 
                                onClick={runScopeReview} 
                                disabled={isLoading || !activeProposal}
                            >
                                {isLoading ? 'Analyzing...' : 'Re-run Scope Review'}
                            </Button>
                        </div>
                        <ScopeView data={analysisResult.structured_data.scope_data} />
                    </div>
                ) : (
                    <div className="flex flex-col h-full items-center justify-center text-slate-400">
                        <div className="text-center max-w-md">
                            <div className="text-lg font-semibold mb-2 text-slate-700">Scope Review</div>
                            <p className="text-sm mb-4">
                                The Proposal defines what ABS priced and serves as the authoritative baseline.
                            </p>
                            
                            {/* Status indicators */}
                            <div className="mb-6 space-y-2">
                                <div className={`flex items-center justify-center gap-2 text-sm ${activeProposal ? 'text-green-600' : 'text-slate-400'}`}>
                                    {activeProposal ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                                    <span>Proposal: {activeProposal ? activeProposal.filename : 'Not uploaded'}</span>
                                </div>
                                <div className={`flex items-center justify-center gap-2 text-sm ${activeContract ? 'text-green-600' : 'text-yellow-500'}`}>
                                    {activeContract ? <CheckCircle size={16} /> : <Clock size={16} />}
                                    <span>Contract: {activeContract ? activeContract.filename : 'Optional - for comparison'}</span>
                                </div>
                            </div>
                            
                            {/* Run Scope Review Button */}
                            <Button 
                                onClick={runScopeReview} 
                                disabled={isLoading || !activeProposal}
                                
                            >
                                {isLoading ? (
                                    <span className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Analyzing...
                                    </span>
                                ) : (
                                    'Run Scope Review'
                                )}
                            </Button>
                            
                            {!activeProposal && (
                                <p className="text-xs text-slate-500 mt-4">
                                    Upload a Proposal with the blue "Proposal" button above.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Exclusions Tab */}
            <div className={`absolute inset-0 overflow-y-auto p-4 bg-white transition-opacity duration-200 ${activeTab === 'exclusions' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`} data-ca-pane="exclusions">
                {analysisResult?.structured_data?.exclusions_data ? (
                    <div className="max-w-4xl mx-auto">
                        <ExclusionsView 
                            exclusionsData={analysisResult.structured_data.exclusions_data}
                            onCopyToEmail={(content) => {
                                // Switch to email tab and append content
                                setActiveTab('email');
                            }}
                        />
                    </div>
                ) : (
                    <div className="flex flex-col h-full items-center justify-center text-slate-400">
                        <div className="text-center max-w-md">
                            <FileText size={48} className="mx-auto mb-4 opacity-50" />
                            <div className="text-lg font-semibold mb-2 text-slate-700">Proposal Exclusions</div>
                            <p className="text-sm mb-4">
                                Exclusions will be extracted from the proposal document.
                            </p>
                            <div className={`flex items-center justify-center gap-2 text-sm ${activeProposal ? 'text-green-600' : 'text-slate-400'}`}>
                                {activeProposal ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                                <span>Proposal: {activeProposal ? activeProposal.filename : 'Not uploaded'}</span>
                            </div>
                            {!activeProposal && (
                                <p className="text-xs text-slate-500 mt-4">
                                    Upload a Proposal document to extract exclusions.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Email Tab */}
            <div className={`absolute inset-0 overflow-y-auto p-4 bg-white transition-opacity duration-200 ${activeTab === 'email' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`} data-ca-pane="email">
                <div className="max-w-4xl mx-auto">
                    <EmailView 
                        termsData={analysisResult?.structured_data?.negotiation_summary}
                        exclusionsData={analysisResult?.structured_data?.exclusions_data}
                        projectName={analysisResult?.structured_data?.summary_data?.project_name}
                        isNewClient={isNewClient}
                    />
                </div>
            </div>

            {/* History Tab */}
            <div className={`absolute inset-0 overflow-y-auto p-4 bg-white transition-opacity duration-200 ${activeTab === 'history' ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`} data-ca-pane="history">
                <div className="max-w-4xl mx-auto">
                    <HistoryView 
                        reviews={reviewHistory}
                        onLoadReview={loadReviewFromHistory}
                        onDeleteReview={deleteReviewFromHistory}
                        onRefresh={loadHistory}
                        isLoading={isHistoryLoading}
                    />
                </div>
            </div>

        </div>
        {viewer && <DocViewer key={viewer.file_id} doc={viewer} width={paneWidth} onWidth={setPaneWidth}
                              available={paneDocs} onSwitch={(t) => paneDocs[t] && setViewer({ ...paneDocs[t], type: t })}
                              onClose={() => { setViewer(null); setPaneClosed(true); }} />}
        </div>
      </div>
      </div>
    </div>
  );
}
